type Props = {
  // Ascending P10..P90 fan. null → nothing to draw.
  fan: number[] | null;
  // Shared y-domain [min, max] so T and T-1 sparklines are visually comparable.
  domain?: [number, number];
  color?: string;
  width?: number;
  height?: number;
};

// Tiny inline SVG sparkline of a forecast's quantile fan (P10→P90). Drawn as a
// filled area under the curve so the distribution's shape and spread read at a
// glance; no axes/labels (the row's mean column carries the magnitude).
export function QuantileSparkline({ fan, domain, color = '#274483', width = 72, height = 24 }: Props) {
  if (!fan || fan.length < 2) {
    return <span className="text-gray-300">—</span>;
  }

  const lo = domain ? domain[0] : Math.min(...fan);
  const hi = domain ? domain[1] : Math.max(...fan);
  const range = hi - lo || 1;

  const pad = 1;
  const innerH = height - pad * 2;
  const stepX = width / (fan.length - 1);
  const points = fan.map((v, i) => {
    const x = i * stepX;
    const y = pad + innerH - ((v - lo) / range) * innerH;
    return [x, y] as const;
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      role="img"
      aria-label="Forecast distribution shape (P10 to P90)"
    >
      <path d={area} fill={color} fillOpacity={0.12} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.25} strokeLinejoin="round" />
    </svg>
  );
}
